import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { createCategory, getCategoryById, updateCategory } from '../services/categories';

export const CategoryAddPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      const fetchCategory = async () => {
        try {
          const data = await getCategoryById(id);
          const category = Array.isArray(data) ? data[0] : (data.category || data);
          setFormData({
            name: category.name,
            type: category.type,
          });
          setErrors({});
        } catch (error) {
          setErrors({ submit: error.message || 'Failed to load category' });
        } finally {
          setPageLoading(false);
        }
      };
      fetchCategory();
    } else {
      setPageLoading(false);
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Category name is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await updateCategory(id, formData);
      } else {
        await createCategory(formData);
      }
      navigate('/categories');
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <AuthenticatedLayout>
        <div className="space-y-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
          {errors.submit && (
            <div className="max-w-2xl mx-auto">
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {errors.submit}
              </div>
            </div>
          )}
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl px-4 sm:px-6 lg:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
          {isEditMode ? 'Edit Category' : 'Add New Category'}
        </h1>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput
              label="Category Name"
              type="text"
              name="name"
              placeholder="e.g., Office Supplies"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
            />

            <SelectInput
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={[
                { label: 'Income', value: 'income' },
                { label: 'Expense', value: 'expense' },
              ]}
            />

            {errors.submit && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {errors.submit}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Category' : 'Add Category')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/categories')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
};
